import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { ShieldCheck, Clock, ArrowRight, AlertCircle } from 'lucide-react';
import { billingApi } from '@/api/billing';

// STAGE 5 — 4,000 Early-Adopter Lifetime Pass scarcity banner (Pool 1). Renders
// only while the per-user 60-day window is 'active'. "Accept Pass" routes through
// the SPA /api/billing/checkout pipeline (lookup_key: lifetime_pass, $1,000 USD).
export default function S20ScarcityBanner({ passData, onDismissBanner }) {
  // Hard gate: hide for lifetime holders, ineligible (founder), sold-out or expired windows.
  const gated = !passData || ['accepted', 'expired', 'ineligible', 'sold_out'].includes(passData.offerStatus);

  const [timeLeft, setTimeLeft] = useState('');
  const [isDismissed, setIsDismissed] = useState(passData?.offerStatus === 'hidden');
  const [busy, setBusy] = useState(false);

  // Strict 60-Day tick, synced every minute (S20 runtime friendly).
  useEffect(() => {
    if (gated || !passData?.expiresAt) return;
    const calc = () => {
      const diff = new Date(passData.expiresAt).getTime() - Date.now();
      if (diff <= 0) { setTimeLeft('EXPIRED'); return; }
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      setTimeLeft(`${days}d ${hours}h remaining`);
    };
    calc();
    const id = setInterval(calc, 60000);
    return () => clearInterval(id);
  }, [gated, passData?.expiresAt]);

  if (gated || isDismissed) return null;

  const handleReject = async () => {
    setIsDismissed(true);
    if (onDismissBanner) await onDismissBanner(passData.id, 'hidden');
  };

  const handleAccept = async () => {
    setBusy(true);
    try {
      const { checkout_url } = await billingApi.checkout({
        lookup_key: 'lifetime_pass',
        origin_url: window.location.origin,
      });
      if (checkout_url) window.location.href = checkout_url;
      else throw new Error('no url');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start checkout');
      setBusy(false);
    }
  };

  const remaining = passData.seats_remaining ?? 4000;

  return (
    <div
      className="w-full bg-gradient-to-r from-purple-950 via-black to-zinc-950 border-b border-[#FFCC00]/50 p-4 select-none animate-[s20BannerIn_0.4s_ease-out]"
      data-testid="scarcity-banner"
    >
      {/* ─── BANNER HEADER BLOCK ─── */}
      <div className="flex items-start gap-3 mb-3">
        <ShieldCheck className="w-5 h-5 text-[#FFCC00] shrink-0 mt-0.5 animate-pulse" />
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="text-white text-xs font-black tracking-widest uppercase">
              Exclusive Early Adopter Offer
            </h4>
            <span className="text-[8px] bg-red-950 text-red-400 border border-red-900 font-bold px-1.5 py-0.5 rounded" data-testid="scarcity-pool-tag">
              POOL 1 SCARCITY
            </span>
          </div>
          <p className="text-zinc-300 text-[10px] font-medium leading-relaxed mt-1" data-testid="scarcity-copy">
            Claim 1 of <span className="text-[#FFCC00] font-black" data-testid="scarcity-seats-remaining">{remaining.toLocaleString()}</span> remaining global Lifetime Passes ($1,000 USD). Unlocks all app desks permanently ad-free.
          </p>
        </div>
      </div>

      {/* ─── TIMING CLOCK & THUMB INTERACTION DECK (44px targets) ─── */}
      <div className="flex items-center justify-between bg-black/40 border border-zinc-900 rounded-xl p-2.5">
        <div className="flex items-center gap-2">
          <Clock className="w-3.5 h-3.5 text-[#00E5FF]" />
          <div>
            <span className="text-zinc-400 text-[8px] font-bold block uppercase leading-none">Offer Window</span>
            <span className="text-white text-[11px] font-mono font-black tracking-wide mt-0.5 block" data-testid="scarcity-countdown">
              {timeLeft}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleReject}
            data-testid="scarcity-reject-btn"
            className="text-zinc-400 hover:text-white text-[10px] font-black uppercase tracking-wider px-2 py-2"
          >
            Reject
          </button>
          <button
            type="button"
            onClick={handleAccept}
            disabled={busy}
            data-testid="scarcity-accept-btn"
            className="bg-[#FFCC00] hover:bg-white text-black font-black text-[10px] px-3.5 h-9 rounded-lg tracking-wider uppercase flex items-center gap-1.5 transition-colors transform active:scale-95 disabled:opacity-60"
          >
            {busy ? 'Loading…' : 'Accept Pass'}
            <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      </div>

      {/* Silent Countdown Disclaimer */}
      <div className="flex items-center gap-1 text-[8px] text-zinc-500 font-bold uppercase tracking-wide justify-center mt-2">
        <AlertCircle className="w-2.5 h-2.5" />
        Rejecting hides the banner layout — 60-day profile clock remains active.
      </div>

      <style>{`@keyframes s20BannerIn { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </div>
  );
}
